import React, { useState, useCallback, useEffect } from "react";
import axios from "axios";
import {
  Upload,
  FileSpreadsheet,
  Users,
  BookOpen,
  MessageSquare,
  Check,
  AlertCircle,
  Loader2,
  Eye,
  RefreshCw,
  UserPlus,
  FileText,
  X,
  Download,
  Trash2,
  UserCog,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const uploadTypes = [
  {
    id: "students",
    title: "Student Data",
    description: "Upload student profiles. New students will be appended to existing data.",
    icon: Users,
    endpoint: "/upload/students",
    acceptFormats: ".xlsx, .xls, .csv",
    color: "text-[#E8B923]",
    bgColor: "bg-[#E8B923]/10",
    canDelete: true,
    canDownload: true,
  },
  {
    id: "mentors",
    title: "Mentors",
    description: "Mentor Union export (CSV or Excel). Existing reports pick up the new mentors automatically.",
    icon: Users,
    endpoint: "/upload/mentors",
    acceptFormats: ".csv, .xlsx",
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    canDelete: true,
    canDownload: true,
    refreshOnUpload: true,
  },
  {
    id: "courses",
    title: "Course Roadmap",
    description: "Curriculum timetable (one tab per program, e.g. PGP YLC and PGP TBM, with Term 1, Term 2... columns). Existing reports are updated automatically.",
    icon: BookOpen,
    endpoint: "/upload/courses",
    acceptFormats: ".xlsx",
    color: "text-emerald-400",
    bgColor: "bg-emerald-500/10",
    canDelete: true,
    canDownload: true,
    refreshOnUpload: true,
  },
  {
    id: "counselling",
    title: "Counselling Material",
    description: "Domain counselling workbook (one tab per domain: PM, Consulting, Data...). Prep roadmaps and course plans in existing reports are updated automatically.",
    icon: MessageSquare,
    endpoint: "/upload/counselling",
    acceptFormats: ".xlsx",
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    canDelete: true,
    canDownload: true,
    refreshOnUpload: true,
  },
];

// Full Data Preview Modal Component
function DataPreviewModal({ type, data, title, isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState("table");

  if (!data || data.length === 0) {
    return null;
  }

  const columns = data.length > 0 ? Object.keys(data[0]).filter(k => !k.startsWith('_') && k !== 'raw_data') : [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] max-h-[90vh] bg-gray-50 border-gray-200">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#E8B923]" />
            {title} - Full Preview ({data.length} records)
          </DialogTitle>
        </DialogHeader>
        
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="bg-white border border-gray-200 p-1.5 gap-1" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
            <TabsTrigger value="table" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
              Table View
            </TabsTrigger>
            <TabsTrigger value="cards" className="data-[state=active]:bg-[#2C3E50] data-[state=active]:text-white data-[state=active]:shadow-sm text-gray-500 rounded-md px-4 py-2 text-sm font-medium transition-all">
              Card View
            </TabsTrigger>
          </TabsList>

          <TabsContent value="table" className="mt-4">
            <ScrollArea className="h-[60vh] rounded-lg border border-gray-200">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow className="border-gray-200">
                    <TableHead className="text-gray-500 text-xs">#</TableHead>
                    {columns.slice(0, 10).map((col) => (
                      <TableHead key={col} className="text-gray-500 text-xs whitespace-nowrap">
                        {col.replace(/_/g, ' ').substring(0, 20)}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((row, idx) => (
                    <TableRow key={idx} className="border-gray-200 hover:bg-gray-50">
                      <TableCell className="text-gray-500 text-xs">{idx + 1}</TableCell>
                      {columns.slice(0, 10).map((col) => (
                        <TableCell key={col} className="text-gray-700 text-xs max-w-[200px] truncate">
                          {typeof row[col] === 'object' ? JSON.stringify(row[col]).substring(0, 50) : String(row[col] || '').substring(0, 50)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="cards" className="mt-4">
            <ScrollArea className="h-[60vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-2">
                {data.slice(0, 50).map((row, idx) => (
                  <Card key={idx} className="bg-white border-gray-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <Badge className="bg-[#E8B923]/20 text-[#E8B923]">#{idx + 1}</Badge>
                      </div>
                      <div className="space-y-1">
                        {columns.slice(0, 6).map((col) => (
                          <div key={col} className="flex justify-between text-xs">
                            <span className="text-gray-500">{col.replace(/_/g, ' ').substring(0, 15)}:</span>
                            <span className="text-gray-700 text-right max-w-[60%] truncate">
                              {typeof row[col] === 'object' ? '...' : String(row[col] || '-').substring(0, 30)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

// Single Student Upload Modal
function AddStudentModal({ isOpen, onClose, onSuccess }) {
  const [programType, setProgramType] = useState("PGP_TBM");
  const [resumeFile, setResumeFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!resumeFile) {
      toast.error("Please select a resume file");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("resume", resumeFile);
    formData.append("program_type", programType);

    try {
      const response = await axios.post(`${API}/upload/student-resume`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      toast.success(`Student "${response.data.student_name}" added successfully!`);
      toast.info(`Primary Domain: ${response.data.primary_domain}`);
      onSuccess();
      onClose();
      setResumeFile(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add student");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-50 border-gray-200">
        <DialogHeader>
          <DialogTitle className="text-gray-900 flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-[#E8B923]" />
            Add Single Student
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            Upload a resume and we'll automatically parse it to create a student profile and generate their career report.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          <div className="space-y-2">
            <Label className="text-gray-700">Program Type</Label>
            <Select value={programType} onValueChange={setProgramType}>
              <SelectTrigger className="bg-white border-gray-200 text-gray-900">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="PGP_TBM" className="text-gray-900">PGP TBM</SelectItem>
                <SelectItem value="PGP_YLC" className="text-gray-900">PGP YLC</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-700">Resume File</Label>
            <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center hover:border-[#E8B923]/50 transition-all">
              {resumeFile ? (
                <div className="flex items-center justify-center gap-3">
                  <FileText className="h-6 w-6 text-[#E8B923]" />
                  <span className="text-gray-700">{resumeFile.name}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setResumeFile(null)}
                    className="text-gray-500 hover:text-red-400"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-gray-500 mx-auto mb-2" />
                  <p className="text-gray-500 mb-2">Drop resume here or</p>
                  <label>
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.docx,.txt"
                      onChange={(e) => setResumeFile(e.target.files[0])}
                      data-testid="resume-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-gray-200 text-gray-700"
                      onClick={(e) => e.target.closest("label")?.querySelector("input")?.click()}
                      data-testid="browse-resume-btn"
                    >
                      Browse Files
                    </Button>
                  </label>
                  <p className="text-xs text-gray-400 mt-2">Supported: PDF, DOCX, TXT</p>
                </>
              )}
            </div>
          </div>

          <div className="flex gap-3 justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-gray-200 text-gray-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#E8B923] text-black hover:bg-[#D4A017]"
              disabled={uploading || !resumeFile}
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing Resume...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Student
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function DataUpload() {
  const [uploadStatus, setUploadStatus] = useState({});
  const [dragOver, setDragOver] = useState(null);
  const [uploadData, setUploadData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [previewModal, setPreviewModal] = useState({ isOpen: false, type: '', data: [], title: '' });
  const [addStudentModal, setAddStudentModal] = useState(false);

  useEffect(() => {
    fetchUploadStatus();
  }, []);

  const fetchUploadStatus = async () => {
    try {
      setLoadingPreview(true);
      const response = await axios.get(`${API}/uploads/status`);
      setUploadData(response.data);
    } catch (error) {
      console.error("Failed to fetch upload status:", error);
    } finally {
      setLoadingPreview(false);
    }
  };

  // Rebuilds the course/prep/mentor sections of existing reports from the latest uploads.
  // No AI calls, so it is free and takes seconds.
  const handleRefreshReports = async () => {
    setRegenerating(true);
    try {
      const response = await axios.post(`${API}/students/refresh-reports`);
      toast.success(response.data.message);
      (response.data.warnings || []).forEach((w) => toast.warning(w));
      fetchUploadStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to refresh reports");
    } finally {
      setRegenerating(false);
    }
  };

  const handleDeleteData = async (dataType) => {
    if (!window.confirm(`Are you sure you want to delete all ${dataType} data? This cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/data/${dataType}`);
      toast.success(`${dataType} data deleted successfully`);
      fetchUploadStatus();
    } catch (error) {
      toast.error(`Failed to delete ${dataType} data`);
    }
  };

  const handleDownloadData = async (dataType) => {
    try {
      const response = await axios.get(`${API}/data/${dataType}/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${dataType}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${dataType} data exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${dataType} data`);
    }
  };

  const handleFileUpload = async (file, uploadType) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploadStatus((prev) => ({
      ...prev,
      [uploadType.id]: { status: "uploading", progress: 0 },
    }));

    try {
      const progressInterval = setInterval(() => {
        setUploadStatus((prev) => {
          const current = prev[uploadType.id];
          if (current && current.progress < 90) {
            return {
              ...prev,
              [uploadType.id]: {
                ...current,
                progress: current.progress + 10,
              },
            };
          }
          return prev;
        });
      }, 500);

      const response = await axios.post(`${API}${uploadType.endpoint}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      clearInterval(progressInterval);

      setUploadStatus((prev) => ({
        ...prev,
        [uploadType.id]: {
          status: "success",
          progress: 100,
          message: response.data.message,
          count: response.data.count,
          reportsGenerated: response.data.reports_generated,
        },
      }));

      toast.success(response.data.message);
      fetchUploadStatus();
      
      // Reference data changed: update existing reports without re-running the AI
      if (uploadType.refreshOnUpload) {
        setTimeout(() => handleRefreshReports(), 500);
      }
    } catch (error) {
      setUploadStatus((prev) => ({
        ...prev,
        [uploadType.id]: {
          status: "error",
          progress: 0,
          message: error.response?.data?.detail || "Upload failed",
        },
      }));
      toast.error(error.response?.data?.detail || "Upload failed");
    }
  };

  const handleDrop = useCallback((e, uploadType) => {
    e.preventDefault();
    setDragOver(null);
    const file = e.dataTransfer.files[0];
    handleFileUpload(file, uploadType);
  }, []);

  const handleDragOver = useCallback((e, id) => {
    e.preventDefault();
    setDragOver(id);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOver(null);
  }, []);

  const handleFileInput = (e, uploadType) => {
    const file = e.target.files[0];
    handleFileUpload(file, uploadType);
    e.target.value = "";
  };

  const openPreviewModal = (type, data, title) => {
    setPreviewModal({ isOpen: true, type, data, title });
  };

  const closePreviewModal = () => {
    setPreviewModal({ isOpen: false, type: '', data: [], title: '' });
  };

  const renderUploadStatus = (uploadType) => {
    const status = uploadStatus[uploadType.id];
    if (!status) return null;

    if (status.status === "uploading") {
      return (
        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-[#E8B923]" />
            <span className="text-sm text-gray-500">
              {uploadType.id === "students" ? "Uploading & generating reports..." : "Uploading..."}
            </span>
          </div>
          <Progress value={status.progress} className="h-2" />
        </div>
      );
    }

    if (status.status === "success") {
      return (
        <div className="mt-4 space-y-1">
          <div className="flex items-center gap-2 text-emerald-400">
            <Check className="h-4 w-4" />
            <span className="text-sm">{status.message}</span>
          </div>
          {status.reportsGenerated > 0 && (
            <p className="text-xs text-gray-500 pl-6">
              {status.reportsGenerated} reports auto-generated
            </p>
          )}
        </div>
      );
    }

    if (status.status === "error") {
      return (
        <div className="mt-4 flex items-center gap-2 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">{status.message}</span>
        </div>
      );
    }
  };

  const getUploadMeta = (typeId) => {
    if (!uploadData?.upload_history) return null;
    return uploadData.upload_history.find((h) => h.type === typeId);
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope text-3xl font-bold text-gray-900">
            Data Upload
          </h1>
          <p className="text-gray-500 mt-1">
            Upload Excel/CSV files to add to the system. New students are appended.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-[#E8B923] text-[#E8B923] hover:bg-[#E8B923]/10"
            onClick={() => setAddStudentModal(true)}
            data-testid="add-single-student-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Add Single Student
          </Button>
          <Button
            variant="outline"
            className="border-gray-200 text-gray-700 hover:bg-gray-100"
            onClick={fetchUploadStatus}
            data-testid="refresh-preview-btn"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Current Data Summary - Clickable Full Preview */}
      <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <CardHeader>
          <CardTitle className="font-manrope text-lg text-gray-900 flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#E8B923]" />
            Currently Uploaded Data
            <span className="text-xs text-gray-500 font-normal ml-2">
              (Click to view full data)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingPreview ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-[#E8B923]" />
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Students */}
              <div 
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:border-[#E8B923]/50 transition-all"
                onClick={() => uploadData?.students?.data && openPreviewModal('students', uploadData.students.data, 'Student Data')}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 text-sm">Students</span>
                  <Badge className="bg-[#E8B923]/20 text-[#E8B923]">
                    {uploadData?.students?.count || 0}
                  </Badge>
                </div>
                {getUploadMeta("students") && (
                  <p className="text-xs text-gray-400 mb-2">
                    File: {getUploadMeta("students").filename}
                  </p>
                )}
                {uploadData?.students?.count > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-[#E8B923] hover:bg-[#E8B923]/10"
                  >
                    <Eye className="h-3 w-3 mr-1" /> View
                  </Button>
                )}
              </div>

              {/* Mentors */}
              <div 
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:border-blue-500/50 transition-all"
                onClick={() => uploadData?.mentors?.data && openPreviewModal('mentors', uploadData.mentors.data, 'Mentor Data')}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 text-sm">Mentors</span>
                  <Badge className="bg-blue-500/20 text-blue-400">
                    {uploadData?.mentors?.count || 0}
                  </Badge>
                </div>
                {getUploadMeta("mentors") && (
                  <p className="text-xs text-gray-400 mb-2">
                    File: {getUploadMeta("mentors").filename}
                  </p>
                )}
                {uploadData?.mentors?.count > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-blue-400 hover:bg-blue-500/10"
                  >
                    <Eye className="h-3 w-3 mr-1" /> View
                  </Button>
                )}
              </div>

              {/* Courses */}
              <div 
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:border-emerald-500/50 transition-all"
                onClick={() => uploadData?.courses?.data && openPreviewModal('courses', uploadData.courses.data, 'Course Roadmap')}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 text-sm">Courses</span>
                  <Badge className="bg-emerald-500/20 text-emerald-400">
                    {uploadData?.courses?.count || 0}
                  </Badge>
                </div>
                {getUploadMeta("courses") && (
                  <p className="text-xs text-gray-400 mb-2">
                    Sheets: {getUploadMeta("courses").sheets?.join(", ") || "N/A"}
                  </p>
                )}
                {uploadData?.courses?.count > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-emerald-400 hover:bg-emerald-500/10"
                  >
                    <Eye className="h-3 w-3 mr-1" /> View
                  </Button>
                )}
              </div>

              {/* Counselling */}
              <div 
                className="p-4 bg-gray-50 border border-gray-200 rounded-lg cursor-pointer hover:border-purple-500/50 transition-all"
                onClick={() => uploadData?.counselling?.data && openPreviewModal('counselling', uploadData.counselling.data, 'Counselling Material')}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-500 text-sm">Counselling</span>
                  <Badge className="bg-purple-500/20 text-purple-400">
                    {uploadData?.counselling?.count || 0}
                  </Badge>
                </div>
                {getUploadMeta("counselling") && (
                  <p className="text-xs text-gray-400 mb-2">
                    Resources: {getUploadMeta("counselling").resources_count || 0}
                  </p>
                )}
                {uploadData?.counselling?.count > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-purple-400 hover:bg-purple-500/10"
                  >
                    <Eye className="h-3 w-3 mr-1" /> View
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Preview Modal */}
      <DataPreviewModal
        type={previewModal.type}
        data={previewModal.data}
        title={previewModal.title}
        isOpen={previewModal.isOpen}
        onClose={closePreviewModal}
      />

      {/* Add Student Modal */}
      <AddStudentModal
        isOpen={addStudentModal}
        onClose={() => setAddStudentModal(false)}
        onSuccess={fetchUploadStatus}
      />

      {/* Upload Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {uploadTypes.map((uploadType) => {
          const dataCount = uploadData?.[uploadType.id === 'internal_mentors' ? 'internal_mentors' : uploadType.id]?.count || 0;
          const hasData = dataCount > 0;
          
          return (
            <Card
              key={uploadType.id}
              className={`bg-white border-gray-200 transition-all duration-300 ${
                dragOver === uploadType.id ? "border-[#E8B923] bg-[#E8B923]/5" : ""
              }`}
              onDrop={(e) => handleDrop(e, uploadType)}
              onDragOver={(e) => handleDragOver(e, uploadType.id)}
              onDragLeave={handleDragLeave}
              data-testid={`upload-card-${uploadType.id}`}
            >
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-lg ${uploadType.bgColor}`}>
                      <uploadType.icon className={`h-5 w-5 ${uploadType.color}`} />
                    </div>
                    <div>
                      <CardTitle className="font-manrope text-lg text-gray-900 flex items-center gap-2">
                        {uploadType.title}
                        {hasData && (
                          <Badge className={`${uploadType.bgColor} ${uploadType.color} text-xs`}>
                            {dataCount}
                          </Badge>
                        )}
                      </CardTitle>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {uploadType.description}
                      </p>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Action Buttons Row */}
                {hasData && (uploadType.canDelete || uploadType.canDownload) && (
                  <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                    {uploadType.canDownload && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-gray-200 text-gray-700 hover:bg-gray-100 flex-1"
                        onClick={() => handleDownloadData(uploadType.id === 'internal_mentors' ? 'internal_mentors' : uploadType.id)}
                        data-testid={`download-btn-${uploadType.id}`}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Export CSV
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-gray-700 hover:bg-gray-100"
                      onClick={() => {
                        const data = uploadData?.[uploadType.id === 'internal_mentors' ? 'internal_mentors' : uploadType.id]?.data;
                        if (data) openPreviewModal(uploadType.id, data, uploadType.title);
                      }}
                      data-testid={`preview-btn-${uploadType.id}`}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Preview
                    </Button>
                    {uploadType.canDelete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                        onClick={() => handleDeleteData(uploadType.id === 'internal_mentors' ? 'internal_mentors' : uploadType.id)}
                        data-testid={`delete-btn-${uploadType.id}`}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete All
                      </Button>
                    )}
                  </div>
                )}
                
                {/* Upload Zone */}
                <div
                  className={`drop-zone ${
                    dragOver === uploadType.id ? "active" : ""
                  }`}
                >
                  <Upload className="h-8 w-8 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-500 mb-2">
                    Drag & drop your file here, or
                  </p>
                  <label>
                    <input
                      type="file"
                      className="hidden"
                      accept={uploadType.acceptFormats}
                      onChange={(e) => handleFileInput(e, uploadType)}
                      data-testid={`file-input-${uploadType.id}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-gray-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                      onClick={(e) => e.target.closest("label")?.querySelector("input")?.click()}
                      data-testid={`browse-btn-${uploadType.id}`}
                    >
                      Browse Files
                    </Button>
                  </label>
                  <p className="text-xs text-gray-400 mt-3">
                    Supported: {uploadType.acceptFormats}
                  </p>
                </div>

                {renderUploadStatus(uploadType)}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Instructions Card */}
      <Card className="bg-white border border-gray-200/80" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <CardHeader>
          <CardTitle className="font-manrope text-lg text-gray-900 flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-[#E8B923]" />
            File Format Guidelines
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Student Data
              </h4>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Include columns: Name, Email, Application Number</li>
                <li>• Work experience, Education, Test scores</li>
                <li>• Interview assessment & interviewer comments</li>
                <li>• <span className="text-emerald-400">New students are appended (not replaced)</span></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Mentor Data
              </h4>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Include: First_Name, Last_Name, Designation</li>
                <li>• Company, Functional_Domain, Expertise</li>
                <li>• LinkedIn, Email, Rating</li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Course Roadmap
              </h4>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Multi-sheet Excel: PGP YLC & PGP TBM tabs</li>
                <li>• Term-wise course distribution (Term 1-8)</li>
                <li>• Courses matched by student program type</li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Counselling Material
              </h4>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Domain-specific sheets (PM, Consulting, etc.)</li>
                <li>• CV samples, portfolio, YouTube channels</li>
                <li>• Books, blogs, skills to learn</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
